import React, { useState, useEffect, Fragment } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  Table, Drawer, Popconfirm, Tooltip, Tree, Space,
} from 'antd';
import {
  EditOutlined, DeleteOutlined, PlusCircleOutlined
} from '@ant-design/icons';
import H3Text from '@Uilib/h3Text';
import H3Image from '@Uilib/h3Image';
import Helpers from '@Apis/helpers';
import H3Button, { defaultButtonTypes } from '@Uilib/h3Button';
import closeIcon from '@Images/icons/icon-close-blue.png';
import CategoryActions from '@Actions/categoryActions';
import CategoryForm from './CategoryForm';
import './style.scss';

const Category = ({
  user,
  deleteCategory,
  getSubCategories,
  subCategories,
  getSubCategoriesLoading,
  deleteCategoryLoading,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isUserReady, setIsUserReady] = useState(false);
  const [showNewCategoryModal, setShowNewCategoryModal] = useState(false);
  const [showUpdateCategoryModal, setShowUpdateCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [createChildCategory, setCreateChildCategory] = useState(null);
  const [showAddChildCategoryModal, setShowAddChildCategoryModal] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    if (user && !isUserReady) {
      getSubCategories();
      setIsUserReady(true);
    }
  }, [user, isUserReady, getSubCategories, currentPage, limit]);

  function buildTree(data) {
    return data?.map((item) => {
      // Construct a key for each item
      const key = `${item.product_category_id}`;

      // Create a node for the current item
      const node = {
        title: item.product_category_name,
        key,
        children: item.sub_categories ? buildTree(item.sub_categories) : [],
      };

      return node;
    });
  }

  const onSelect = (selectedKeys, info) => {
    setExpandedKeys(selectedKeys);
  };

  const handleEdit = (nodeData) => {
    const data = {
      product_category_id: nodeData.key,
      product_category_name: nodeData.title,
    };
    setShowUpdateCategoryModal(true);
    setSelectedCategory(data);
  };

  const handleAddChild = (nodeData) => {
    const data = {
      product_category_id: nodeData.key,
      product_category_name: nodeData.title,
    };
    setShowAddChildCategoryModal(true);
    setCreateChildCategory(data);
  };

  const renderTitle = (nodeData) => (
    <Space className="hover-visible">
      {nodeData.title}
      <Tooltip title="Edit" color="rgb(48, 134, 255)">
        <EditOutlined
          onClick={() => handleEdit(nodeData)}
          style={{ color: 'rgb(48, 134, 255)' }}
        />
      </Tooltip>

      <Popconfirm
        placement="topRight"
        loading={deleteCategoryLoading}
        title={(
          <React.Fragment>
            <p style={{ marginBottom: '6px', fontWeight: 'bold' }}>Are you sure you want to delete this category?</p>
            <p style={{ fontSize: '14px', margin: '0 0 6px 0' }}>
              Deleting this category will also remove all related subcategories.
            </p>
            <p style={{ fontSize: '14px', margin: '0' }}>
              Additionally, any products associated with this category will be unassigned.
            </p>
          </React.Fragment>
        )}
        onConfirm={() => deleteCategory(
          { product_category_id: nodeData?.key },
          () => getSubCategories(),
        )}
        okText="Yes"
        cancelText="No"
      >
        <div>
          <DeleteOutlined style={{ color: 'red' }} />
        </div>
      </Popconfirm>

      <Tooltip
        title="Add Child Category"
        color="rgb(48, 134, 255)"
      >
        <PlusCircleOutlined
          onClick={() => handleAddChild(nodeData)}
          style={{ color: 'rgb(48, 134, 255)' }}
        />
      </Tooltip>
    </Space>
  );

  const applyRenderTitle = (nodes) => nodes?.map((node) => ({
    ...node,
    title: renderTitle(node),
    children: node.children ? applyRenderTitle(node.children) : [],
  }));

  const categoryListColumns = [
    {
      title: 'CATEGORY NAME',
      render: (text, record) => (
        <React.Fragment>
          <Tree
            showLine
            switcherIcon={<PlusCircleOutlined />}
            defaultExpandedKeys={expandedKeys}
            onSelect={onSelect}
            treeData={applyRenderTitle(buildTree([record]))}
          />
        </React.Fragment>
      ),
    },
  ];

  return (
    <Fragment>
      <div className="category__wrapper">
        <div className="ant-row">
          <div className="ant-col-md-14">
            <div className="category__table__wrapper">
              <div className="category__table__data hide__in-mobile">
                <Table
                  title={() => (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      {`${subCategories?.length || '0'} ${
                        subCategories?.length > 1 ? 'Product Categories' : 'Product Category'
                      }`}
                      <Fragment>
                        {Helpers.getPermission(
                          Helpers.permissionEntities.VENDORS,
                          Helpers.permissionTypes.CREATE,
                          user,
                        ) && (
                          <H3Button
                            buttonType={defaultButtonTypes.BLUE_ROUNDED}
                            text="+ New Category"
                            onClick={() => setShowNewCategoryModal(true)}
                            customClass="category__header__add-button hide__in-mobile"
                          />
                        )}
                      </Fragment>
                    </div>
                  )}
                  loading={getSubCategoriesLoading || deleteCategoryLoading}
                  showHeader
                  size="small"
                  scroll="scroll"
                  columns={categoryListColumns}
                  dataSource={subCategories || []}
                  pagination={subCategories?.length > 10 ? {
                    current: currentPage,
                    total: subCategories?.length ? subCategories?.length : 0,
                    pageSize: limit,
                    position: ['bottomRight'],
                    showSizeChanger: false,
                    onChange: (pg, size) => {
                      setLimit(size);
                      setCurrentPage(pg);
                    },
                  } : false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Drawer
        open={showNewCategoryModal}
        width="360px"
        mask
        onClose={() => {
          setShowNewCategoryModal(false);
          setSelectedCategory(null);
        }}
        destroyOnClose
      >
        <div className="custom-drawer__header-wrapper">
          <div className="custom-drawer__header" style={{ width: '315px' }}>
            <H3Text text="Add New Category" className="custom-drawer__title" />
            <H3Image src={closeIcon} className="custom-drawer__close-icon" onClick={() => setShowNewCategoryModal(false)} />
          </div>
        </div>
        <div className="create-po__new-vendor-wrapper">
          <CategoryForm
            callback={() => {
              getSubCategories();
              setShowNewCategoryModal(false);
            }}
          />
        </div>
      </Drawer>
      <Drawer
        title={!showUpdateCategoryModal ? 'Add Child Category' : 'Update Category'}
        open={showUpdateCategoryModal || showAddChildCategoryModal}
        onClose={() => (showUpdateCategoryModal ? setShowUpdateCategoryModal(false) : setShowAddChildCategoryModal(false))}
        width="360px"
        destroyOnClose
      >
        <div className="custom-drawer__header-wrapper">
          <div className="custom-drawer__header" style={{ width: '315px' }}>
            <H3Text text={!showUpdateCategoryModal ? 'Add Child Category' : 'Update Category'} className="custom-drawer__title" />
            <H3Image src={closeIcon} className="custom-drawer__close-icon" onClick={() => (showUpdateCategoryModal ? setShowUpdateCategoryModal(false) : setShowAddChildCategoryModal(false))} />
          </div>
        </div>
        { (showUpdateCategoryModal) ? (
          <CategoryForm
            selectedCategory={selectedCategory}
            callback={() => {
              getSubCategories();
              setShowUpdateCategoryModal(false);
            }}
          />
        )
          : (
            <CategoryForm
              createChildCategory={createChildCategory}
              callback={() => {
                getSubCategories();
                setShowAddChildCategoryModal(false);
              }}
            />
          )}
      </Drawer>
    </Fragment>
  );
};

const mapStateToProps = ({ UserReducers, CategoryReducers }) => ({
  user: UserReducers.user,
  subCategories: CategoryReducers.subCategories,
  getSubCategoriesLoading: CategoryReducers.getSubCategoriesLoading,
  deleteCategoryLoading: CategoryReducers.deleteCategoryLoading,
});

const mapDispatchToProps = (dispatch) => ({
  deleteCategory: (payload, callback) => dispatch(CategoryActions.deleteCategory(payload, callback)),
  getSubCategories: (searchKeyword, page, limit) => dispatch(CategoryActions.getSubCategories(searchKeyword, page, limit)),
});


export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Category));
